import { useListSnippets } from "@workspace/api-client-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Puzzle } from "lucide-react";
import { Link } from "wouter";

export default function SnippetsPage() {
  const snippetsQuery = useListSnippets();

  if (snippetsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="snippets-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const all = snippetsQuery.data ?? [];
  const builtins = all.filter((s) => s.isBuiltin);
  const owned = all.filter((s) => !s.isBuiltin);

  return (
    <div className="space-y-6" data-testid="snippets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Puzzle className="h-6 w-6" /> Snippets
          </h1>
          <p className="text-muted-foreground">
            Reusable HTML fragments you can embed in any template via{" "}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{`{{> slug}}`}</code>.
          </p>
        </div>
        <Link href="/app/snippets/new">
          <Button data-testid="new-snippet-button">
            <Plus className="h-4 w-4 mr-2" /> New snippet
          </Button>
        </Link>
      </div>

      <section>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Built-in
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {builtins.map((s) => (
            <SnippetCard key={s.id} snippet={s} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Your snippets
        </h2>
        {owned.length === 0 ? (
          <div className="text-sm text-muted-foreground border border-dashed rounded-md p-6 text-center">
            You haven't created any custom snippets yet. Use{" "}
            <Link href="/app/snippets/new" className="underline">
              New snippet
            </Link>{" "}
            to add one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {owned.map((s) => (
              <SnippetCard key={s.id} snippet={s} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SnippetCard({
  snippet,
}: {
  snippet: {
    id: number;
    name: string;
    slug: string;
    description: string;
    isBuiltin: boolean;
    usedByCount: number;
  };
}) {
  return (
    <Link href={`/app/snippets/${snippet.id}`}>
      <Card
        className="cursor-pointer hover:border-primary transition-colors h-full"
        data-testid={`snippet-card-${snippet.slug}`}
      >
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              {snippet.name}
              {snippet.isBuiltin && (
                <Badge variant="secondary" className="text-[10px]">
                  Built-in
                </Badge>
              )}
            </CardTitle>
          </div>
          <CardDescription className="line-clamp-2 min-h-[2.5em]">
            {snippet.description || "No description"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <code className="bg-muted px-1.5 py-0.5 rounded">{`{{> ${snippet.slug}}}`}</code>
            <span data-testid={`snippet-used-by-${snippet.slug}`}>
              {snippet.usedByCount === 0
                ? "Not used yet"
                : `Used by ${snippet.usedByCount} template${snippet.usedByCount === 1 ? "" : "s"}`}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
